﻿import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  alterarUsuario,
  alternarUsuario,
  criarUsuario,
  listarUsuarios,
  redefinirSenha,
  type DadosDeUsuario,
} from '@/features/cadastros/api/cadastrosApi';
import { chaveDeReferencias, useResolvedores } from '@/features/referencias/useReferencias';
import { useSessao } from '@/features/autenticacao/useSessao';
import { descreverErro } from '@/lib/erros';
import { formatarDataHora } from '@/lib/formato';
import type { PerfilCodigo, UsuarioCriado, UsuarioNaLista } from '@/types/api';

import estilos from './Administracao.module.css';

/**
 * Cadastro de usuarios.
 *
 * A SENHA TEMPORARIA APARECE UMA UNICA VEZ, e a tela diz isso. Nao e armazenada em claro e
 * nao ha como recupera-la: o caminho para outra e redefinir. Sem servico de e-mail nesta
 * versao (Decisao 12), o repasse e manual.
 *
 * Desativar REVOGA as sessoes do usuario, no servidor, na mesma gravacao. A tela avisa
 * antes, porque a pessoa pode estar com o sistema aberto neste momento.
 */

const chaveDeUsuarios = ['usuarios'] as const;

const perfis: { valor: PerfilCodigo; rotulo: string }[] = [
  { valor: 'Admin', rotulo: 'Administrador' },
  { valor: 'GestorCs', rotulo: 'Gestor CS' },
  { valor: 'AnalistaCs', rotulo: 'Analista CS' },
  { valor: 'GestorProduto', rotulo: 'Gestor Produto' },
  { valor: 'AnalistaProduto', rotulo: 'Analista Produto' },
  { valor: 'AreaApoio', rotulo: 'Área de apoio' },
  { valor: 'Leitura', rotulo: 'Somente leitura' },
];

export function PaginaDeUsuarios() {
  const queryClient = useQueryClient();
  const { usuario: eu } = useSessao();
  const { areasAtivas, nomeDaArea } = useResolvedores();

  const { data, error, isPending } = useQuery({
    queryKey: chaveDeUsuarios,
    queryFn: ({ signal }) => listarUsuarios(signal),
  });

  const [emEdicao, definirEmEdicao] = useState<number | null>(null);
  const [nome, definirNome] = useState('');
  const [email, definirEmail] = useState('');
  const [perfil, definirPerfil] = useState<PerfilCodigo>('AnalistaCs');
  const [areas, definirAreas] = useState<number[]>([]);
  const [falha, definirFalha] = useState<string | null>(null);
  const [credencial, definirCredencial] = useState<UsuarioCriado | null>(null);

  const recarregar = async () => {
    await queryClient.invalidateQueries({ queryKey: chaveDeUsuarios });
    await queryClient.invalidateQueries({ queryKey: chaveDeReferencias });
  };

  const limpar = () => {
    definirEmEdicao(null);
    definirNome('');
    definirEmail('');
    definirPerfil('AnalistaCs');
    definirAreas([]);
  };

  const salvar = useMutation({
    mutationFn: (dados: DadosDeUsuario) =>
      emEdicao === null
        ? criarUsuario(dados)
        : alterarUsuario(emEdicao, dados).then<UsuarioCriado | null>(() => null),
    onSuccess: async (resultado) => {
      definirFalha(null);

      // Criacao devolve a senha; edicao nao. O null distingue os dois casos.
      if (resultado !== null) {
        definirCredencial(resultado);
      }

      limpar();
      await recarregar();
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const alternar = useMutation({
    mutationFn: ({ id, ativar }: { id: number; ativar: boolean }) => alternarUsuario(id, ativar),
    onSuccess: async () => {
      definirFalha(null);
      await recarregar();
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const redefinir = useMutation({
    mutationFn: (id: number) => redefinirSenha(id),
    onSuccess: async (resultado) => {
      definirFalha(null);
      definirCredencial(resultado);
      await recarregar();
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const editar = (usuario: UsuarioNaLista) => {
    definirEmEdicao(usuario.id);
    definirNome(usuario.nome);
    definirEmail(usuario.email);
    definirPerfil(usuario.perfil);
    definirAreas(usuario.areas);
  };

  const desativar = (usuario: UsuarioNaLista) => {
    const aviso =
      `Desativar ${usuario.nome}?\n\n` +
      'As sessoes abertas dele serao encerradas imediatamente. ' +
      'Se estiver com o sistema aberto agora, sera desconectado.';

    if (window.confirm(aviso)) {
      alternar.mutate({ id: usuario.id, ativar: false });
    }
  };

  const pedirRedefinicao = (usuario: UsuarioNaLista) => {
    const aviso =
      `Redefinir a senha de ${usuario.nome}?\n\n` +
      'Uma senha temporaria sera gerada e mostrada uma unica vez. ' +
      'As sessoes dele serao encerradas e a troca sera exigida no proximo acesso.';

    if (window.confirm(aviso)) {
      redefinir.mutate(usuario.id);
    }
  };

  const ocupado = salvar.isPending || alternar.isPending || redefinir.isPending;

  // Admin tem escopo global e nao precisa de area; os demais precisam de ao menos uma.
  const exigeArea = perfil !== 'Admin';

  return (
    <section className={estilos.pagina}>
      <div>
        <h2 className={estilos.titulo}>Usuários</h2>
        <p className={estilos.apoio}>
          Perfil é papel; área é escopo de dados. Os dois eixos são independentes.
        </p>
      </div>

      {falha !== null && (
        <p className={estilos.falha} role="alert">
          {falha}
        </p>
      )}

      {credencial !== null && (
        <section className={estilos.senhaTemporaria} role="alert">
          <strong>Senha temporária de {credencial.usuario.nome}</strong>
          <div>
            <span className={estilos.senhaValor}>{credencial.senhaTemporaria}</span>
          </div>
          <p style={{ margin: 0 }}>
            Copie agora: ela aparece uma única vez e não há como recuperá-la depois. Esta versao nao
            envia e-mail, entao o repasse e manual. O usuario vai trocar a senha no primeiro acesso.
          </p>
          <button
            className={estilos.botaoSecundario}
            type="button"
            style={{ marginTop: 'var(--espaco-2)' }}
            onClick={() => {
              definirCredencial(null);
            }}
          >
            Já copiei
          </button>
        </section>
      )}

      <section className={estilos.cartao}>
        <h2 className={estilos.secao}>{emEdicao === null ? 'Novo usuário' : 'Editar usuário'}</h2>

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            salvar.mutate({ nome, email, perfil, areas });
          }}
        >
          <div className={estilos.linha}>
            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="nomeDoUsuario">
                Nome
              </label>
              <input
                id="nomeDoUsuario"
                className={estilos.entrada}
                value={nome}
                required
                onChange={(evento) => {
                  definirNome(evento.target.value);
                }}
              />
            </div>

            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="emailDoUsuario">
                E-mail
              </label>
              <input
                id="emailDoUsuario"
                className={estilos.entrada}
                type="email"
                value={email}
                required
                onChange={(evento) => {
                  definirEmail(evento.target.value);
                }}
              />
            </div>

            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="perfilDoUsuario">
                Perfil
              </label>
              <select
                id="perfilDoUsuario"
                className={estilos.selecao}
                value={perfil}
                onChange={(evento) => {
                  definirPerfil(evento.target.value as PerfilCodigo);
                }}
              >
                {perfis.map((opcao) => (
                  <option key={opcao.valor} value={opcao.valor}>
                    {opcao.rotulo}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/*
            CAIXAS DE SELEÇÃO, E NÃO `select multiple`.

            Escolher vários num select exige segurar Ctrl, o que quase ninguém descobre
            sozinho — e quem clica na segunda opção sem Ctrl perde a primeira sem aviso. Era
            também o que dava aquela caixa alta com barra de rolagem no meio do formulário.
            A criação de ação já usa caixas de seleção para clientes; aqui fica igual.
          */}
          <fieldset className={estilos.grupo}>
            <legend className={estilos.rotulo}>Áreas</legend>

            <div className={estilos.opcoes}>
              {areasAtivas.map((area) => (
                <label key={area.id} className={estilos.opcao}>
                  <input
                    type="checkbox"
                    checked={areas.includes(area.id)}
                    onChange={() => {
                      definirAreas((atuais) =>
                        atuais.includes(area.id)
                          ? atuais.filter((id) => id !== area.id)
                          : [...atuais, area.id],
                      );
                    }}
                  />
                  {area.nome}
                </label>
              ))}
            </div>

            {/*
              A área não é preferência: é o filtro de dados do usuário. `ComEscopo`, no
              servidor, limita toda consulta às áreas dele — sem nenhuma, a pessoa entra e
              não vê ação alguma. O Admin é a exceção porque tem escopo global.
            */}
            <span className={estilos.ajuda}>
              {exigeArea
                ? 'Ao menos uma: é o que o usuário vai conseguir ver. Sem área, ele entra e a lista aparece vazia.'
                : 'Administrador vê tudo, independente de área. Aqui é opcional.'}
            </span>
          </fieldset>

          <div className={estilos.acoes}>
            {emEdicao !== null && (
              <button className={estilos.botaoSecundario} type="button" onClick={limpar}>
                Desistir
              </button>
            )}

            <button className={estilos.botaoPrimario} type="submit" disabled={ocupado}>
              {emEdicao === null ? 'Criar usuário' : 'Salvar'}
            </button>
          </div>
        </form>
      </section>

      <div className={estilos.moldura}>
        <table className={estilos.tabela}>
          <caption className="apenas-leitor-de-tela">Usuários cadastrados</caption>
          <thead>
            <tr>
              <th scope="col">Nome</th>
              <th scope="col">E-mail</th>
              <th scope="col">Perfil</th>
              <th scope="col">Áreas</th>
              <th scope="col">Situação</th>
              <th scope="col">Último acesso</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {data?.map((usuario) => (
              <tr key={usuario.id}>
                <td>
                  {usuario.nome}
                  {eu?.id === usuario.id && <span className={estilos.ajuda}> (voce)</span>}
                </td>
                <td>{usuario.email}</td>
                <td>
                  <span className={estilos.seloPerfil}>
                    {perfis.find((p) => p.valor === usuario.perfil)?.rotulo ?? usuario.perfil}
                  </span>
                </td>
                <td>
                  {usuario.areas.length === 0
                    ? 'Global'
                    : usuario.areas.map((id) => nomeDaArea(id)).join(', ')}
                </td>
                <td>
                  <span className={usuario.ativo ? estilos.seloAtivo : estilos.seloInativo}>
                    {usuario.ativo ? 'Ativo' : 'Desativado'}
                  </span>
                  {usuario.bloqueado && (
                    <span className={estilos.seloAtencao} style={{ marginLeft: 4 }}>
                      Bloqueado
                    </span>
                  )}
                  {usuario.deveTrocarSenha && (
                    <span className={estilos.seloAtencao} style={{ marginLeft: 4 }}>
                      Troca pendente
                    </span>
                  )}
                </td>
                <td>{formatarDataHora(usuario.ultimoAcesso)}</td>
                <td>
                  <div className={estilos.acoesDaLinha}>
                    <button
                      className={estilos.botaoSecundario}
                      type="button"
                      disabled={ocupado}
                      onClick={() => {
                        editar(usuario);
                      }}
                    >
                      Editar
                    </button>
                    <button
                      className={estilos.botaoSecundario}
                      type="button"
                      disabled={ocupado}
                      onClick={() => {
                        pedirRedefinicao(usuario);
                      }}
                    >
                      Redefinir senha
                    </button>
                    {/*
                      O botao de desativar nao aparece para a propria conta. O servidor
                      tambem recusa; esconder aqui evita o clique que sempre falha.
                    */}
                    {eu?.id !== usuario.id &&
                      (usuario.ativo ? (
                        <button
                          className={estilos.botaoAtencao}
                          type="button"
                          disabled={ocupado}
                          onClick={() => {
                            desativar(usuario);
                          }}
                        >
                          Desativar
                        </button>
                      ) : (
                        <button
                          className={estilos.botaoSecundario}
                          type="button"
                          disabled={ocupado}
                          onClick={() => {
                            alternar.mutate({ id: usuario.id, ativar: true });
                          }}
                        >
                          Reativar
                        </button>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {isPending && <p className={estilos.aviso}>Carregando usuários...</p>}

        {error !== null && (
          <p className={estilos.aviso} role="alert">
            {descreverErro(error)}
          </p>
        )}
      </div>
    </section>
  );
}
